import { useEffect, useId, useRef, useState } from 'react'

import type { CatalogListDto } from '../api/catalogLists'
import { ConfirmDialog } from './ConfirmDialog'
import { IconPencil, IconTrash } from './icons/CatalogActionIcons'

type CatalogListsManagerDialogProps = {
  open: boolean
  lists: CatalogListDto[]
  busy?: boolean
  onClose: () => void
  onCreate: (name: string) => Promise<void>
  onRename: (list: CatalogListDto, name: string) => Promise<void>
  onOpenList: (list: CatalogListDto) => void
  onDelete: (list: CatalogListDto) => Promise<void>
}

/** Manage shared procurement lists without taking users away from the table. */
export const CatalogListsManagerDialog = ({
  open,
  lists,
  busy = false,
  onClose,
  onCreate,
  onRename,
  onOpenList,
  onDelete,
}: CatalogListsManagerDialogProps) => {
  const titleId = useId()
  const nameInputRef = useRef<HTMLInputElement | null>(null)
  const renameInputRef = useRef<HTMLInputElement | null>(null)
  const [name, setName] = useState('')
  const [creating, setCreating] = useState(false)
  const [editingListId, setEditingListId] = useState<number | null>(null)
  const [editName, setEditName] = useState('')
  const [renamingListId, setRenamingListId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<CatalogListDto | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setName('')
      setError(null)
      setDeleting(null)
      setEditingListId(null)
      setEditName('')
      return
    }
    const frame = window.requestAnimationFrame(() => nameInputRef.current?.focus())
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && deleting == null && editingListId == null) {
        event.preventDefault()
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [deleting, editingListId, onClose, open])

  useEffect(() => {
    if (editingListId == null) return
    const frame = window.requestAnimationFrame(() => renameInputRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [editingListId])

  if (!open) return null

  const handleCreate = async () => {
    const trimmed = name.trim()
    if (!trimmed) return
    setCreating(true)
    setError(null)
    try {
      await onCreate(trimmed)
      setName('')
    } catch (createError) {
      setError(createError instanceof Error ? createError.message : 'Не удалось создать список')
    } finally {
      setCreating(false)
    }
  }

  const handleDelete = async () => {
    if (!deleting) return
    setError(null)
    try {
      await onDelete(deleting)
      setDeleting(null)
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : 'Не удалось удалить список')
      setDeleting(null)
    }
  }

  const startRenaming = (list: CatalogListDto) => {
    setError(null)
    setEditingListId(list.id)
    setEditName(list.name)
  }

  const cancelRenaming = () => {
    setEditingListId(null)
    setEditName('')
  }

  const handleRename = async (list: CatalogListDto) => {
    const trimmed = editName.trim()
    if (!trimmed || trimmed === list.name || renamingListId != null) return
    setRenamingListId(list.id)
    setError(null)
    try {
      await onRename(list, trimmed)
      cancelRenaming()
    } catch (renameError) {
      setError(renameError instanceof Error ? renameError.message : 'Не удалось переименовать список')
    } finally {
      setRenamingListId(null)
    }
  }

  return (
    <div
      className="market-dialog-backdrop"
      role="presentation"
      onMouseDown={deleting == null ? onClose : undefined}
    >
      <section
        className="market-dialog market-catalog-lists-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="market-dialog__header">
          <div>
            <h2 id={titleId} className="market-dialog__title">Списки</h2>
            <p className="market-dialog__hint">Нажмите на список, чтобы открыть его позиции в таблице.</p>
          </div>
          <button type="button" className="echat-btn echat-btn--ghost echat-btn--sm" onClick={onClose}>
            Закрыть
          </button>
        </div>

        <div className="market-catalog-list-manager__items" aria-label="Списки организации">
          {lists.map((list) => (
            <div key={list.id} className="market-catalog-list-manager__item">
              {editingListId === list.id ? (
                <div className="market-catalog-list-manager__rename">
                  <input
                    ref={renameInputRef}
                    className="echat-field__input"
                    aria-label={`Новое название списка «${list.name}»`}
                    value={editName}
                    maxLength={80}
                    disabled={renamingListId === list.id}
                    onChange={(event) => setEditName(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter') {
                        event.preventDefault()
                        void handleRename(list)
                      } else if (event.key === 'Escape') {
                        event.preventDefault()
                        cancelRenaming()
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="echat-btn echat-btn--primary echat-btn--sm"
                    disabled={!editName.trim() || editName.trim() === list.name || renamingListId === list.id}
                    onClick={() => void handleRename(list)}
                  >
                    {renamingListId === list.id ? 'Сохраняем…' : 'Сохранить'}
                  </button>
                  <button
                    type="button"
                    className="echat-btn echat-btn--ghost echat-btn--sm"
                    disabled={renamingListId === list.id}
                    onClick={cancelRenaming}
                  >
                    Отмена
                  </button>
                </div>
              ) : (
                <>
                  <button
                    type="button"
                    className="market-catalog-list-manager__open"
                    aria-label={`Открыть список «${list.name}»`}
                    disabled={busy || creating || renamingListId != null}
                    onClick={() => onOpenList(list)}
                  >
                    <span>{list.name}</span>
                    {list.systemKey ? <span className="market-catalog-list-manager__system">Системный</span> : null}
                  </button>
                  <div className="market-catalog-list-manager__actions">
                    {list.systemKey == null ? (
                      <>
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost market-table__icon-btn"
                          aria-label={`Переименовать список «${list.name}»`}
                          title="Переименовать список"
                          disabled={busy || creating || renamingListId != null}
                          onClick={() => startRenaming(list)}
                        >
                          <IconPencil />
                        </button>
                        <button
                          type="button"
                          className="echat-btn echat-btn--ghost market-table__icon-btn market-table__icon-btn--danger"
                          aria-label={`Удалить список «${list.name}»`}
                          title="Удалить список"
                          disabled={busy || creating || renamingListId != null}
                          onClick={() => setDeleting(list)}
                        >
                          <IconTrash />
                        </button>
                      </>
                    ) : null}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>

        <div className="market-catalog-lists-dialog__create">
          <span className="echat-field__label">Новый список</span>
          <div className="market-catalog-lists-dialog__create-row">
            <input
              ref={nameInputRef}
              className="echat-field__input"
              value={name}
              maxLength={80}
              placeholder="Например, Постоянный заказ"
              disabled={busy || creating}
              onChange={(event) => setName(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  void handleCreate()
                }
              }}
            />
            <button
              type="button"
              className="echat-btn echat-btn--primary"
              disabled={!name.trim() || busy || creating}
              onClick={() => void handleCreate()}
            >
              {creating ? 'Создание…' : 'Создать'}
            </button>
          </div>
        </div>
        {error ? <p className="echat-error" role="alert">{error}</p> : null}
      </section>

      <ConfirmDialog
        open={deleting != null}
        title="Удалить список?"
        message={deleting ? 'Список «' + deleting.name + '» и его отметки у позиций будут удалены.' : ''}
        confirmLabel="Удалить"
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleting(null)}
      />
    </div>
  )
}
